import { Pagination } from "types/Models/pagination";
import { emptyApi } from "./emptyApi";
import qs from "qs";
import { Merchant } from "types/Models/merchant";
import { store } from "store/store";
import { patchTransactionInCaches } from "./transactions";

type Txn = {
  groupId: string;
  page_num?: number;
  type?: string;
  search_term?: "CONTRACTOR" | null;
  season: string;
};

const isMerchantListData = (
  data: unknown
): data is { merchants: Merchant[] } => {
  return (
    Boolean(data) && Array.isArray((data as { merchants?: unknown }).merchants)
  );
};

const findCachedMerchantById = (merchantId?: string): Merchant | undefined => {
  if (!merchantId) {
    return undefined;
  }

  const queries = store.getState().api.queries;

  for (const queryData of Object.values(queries)) {
    if (
      queryData?.endpointName !== "getAllTransactionMerchants" ||
      !isMerchantListData(queryData.data)
    ) {
      continue;
    }

    const merchant = queryData.data.merchants.find(
      ({ uuid }) => uuid === merchantId
    );

    if (merchant) {
      return merchant;
    }
  }

  return undefined;
};

export const merchantsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllTransactionMerchants: build.query<
      { merchants: Merchant[] } & Pagination,
      {
        groupId: string;
        entityId: string;
        search_term?: string;
        page_num?: number;
        merchantIds?: string;
      }
    >({
      query: ({ groupId, entityId, search_term, page_num, merchantIds }) => {
        let queryUrl = qs.stringify(
          {
            search_term: search_term || null,
            page_num: page_num,
            merchant_uuids: merchantIds,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/merchants/all/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["TRANSACTION_MERCHANTS"] : []),
    }),

    getMerchantById: build.query<
      {
        logo?: string | null;
        name: string;
        uuid: string;
        currency?: string;
      },
      {
        groupId: string;
        merchantId: string;
      }
    >({
      query: ({ groupId, merchantId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/merchant/${merchantId}/`,
        };
      },
      providesTags: (result) => (result ? ["TRANSACTION_MERCHANTS"] : []),
    }),

    assignExistingMerchant: build.mutation<
      Merchant,
      {
        groupId: string;
        transactionId: string;
        payload: { merchant_id: string };
        skipInvalidateTags?: string[];
      }
    >({
      query: ({ groupId, transactionId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/txn-merchant/${transactionId}/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }

        const allTags = [
          "TRANSACTION_MERCHANTS",
          "BOOKS_SINGLE_TRANSACTION",
          "BOOKS_TRANSACTIONS",
          "VENDORS",
          "CATEGORISATION_HISTORY",
          "CATEGORISATION_HISTORY_TXNS",
        ] as const;

        const tagsToSkip = arg.skipInvalidateTags || [];
        return allTags.filter(
          (tag) => !tagsToSkip.includes(tag)
        ) as (typeof allTags)[number][];
      },
      async onQueryStarted(
        { transactionId, payload },
        { dispatch, queryFulfilled }
      ) {
        const optimisticMerchant = findCachedMerchantById(payload.merchant_id);

        const patchResults = optimisticMerchant
          ? patchTransactionInCaches(dispatch, transactionId, (draft) => {
              draft.merchant = optimisticMerchant.name;
              draft.merchant_uuid = optimisticMerchant.uuid;
              draft.logo = optimisticMerchant.logo || undefined;
            })
          : [];

        try {
          const { data: updatedMerchant } = await queryFulfilled;

          patchTransactionInCaches(dispatch, transactionId, (draft) => {
            draft.merchant = updatedMerchant.name;
            draft.merchant_uuid = updatedMerchant.uuid;
            draft.logo = updatedMerchant.logo || undefined;
          });
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    createMerchant: build.mutation({
      query: ({ groupId, entityId, payload, skipInvalidateTags }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/merchant/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }

        const allTags = [
          "BOOKS_TRANSACTIONS",
          "TRANSACTION_MERCHANTS",
          "VENDORS",
          "CATEGORISATION_HISTORY",
          "CATEGORISATION_HISTORY_TXNS",
        ] as const;

        const tagsToSkip = arg.skipInvalidateTags || [];
        return allTags.filter(
          (tag) => !tagsToSkip.includes(tag)
        ) as (typeof allTags)[number][];
      },
    }),

    deleteMerchantFromTxn: build.mutation<
      void,
      {
        groupId: string;
        transactionId: string;
        skipInvalidateTags?: string[];
      }
    >({
      query: ({ groupId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/txn-merchant/${transactionId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, arg) => {
        const allTags = [
          "TRANSACTION_MERCHANTS",
          "BOOKS_SINGLE_TRANSACTION",
          "BOOKS_TRANSACTIONS",
          "CATEGORISATION_HISTORY",
          "CATEGORISATION_HISTORY_TXNS",
        ] as const;

        const tagsToSkip = arg.skipInvalidateTags || [];
        return allTags.filter(
          (tag) => !tagsToSkip.includes(tag)
        ) as (typeof allTags)[number][];
      },
      async onQueryStarted({ transactionId }, { dispatch, queryFulfilled }) {
        const patchResults = patchTransactionInCaches(
          dispatch,
          transactionId,
          (draft) => {
            draft.merchant = undefined;
            draft.merchant_uuid = undefined;
            draft.logo = undefined;
          }
        );

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    getInfiniteAllTransactionMerchants: build.infiniteQuery<
      { merchants: Merchant[] } & Pagination,
      {
        groupId: string;
        entityId: string;
        search_term?: string;
        merchantIds?: string;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam,
          allPageParams,
          queryArg
        ) => {
          if (lastPageParam < (lastPage.total_pages || 1)) {
            return lastPageParam + 1;
          }
          return undefined;
        },
        getPreviousPageParam: (
          firstPage,
          allPages,
          firstPageParam,
          allPageParams,
          queryArg
        ) => {
          return firstPageParam > 1 ? firstPageParam - 1 : undefined;
        },
      },
      query({ queryArg, pageParam }) {
        const { groupId, entityId, search_term, merchantIds } = queryArg;
        let queryUrl = qs.stringify(
          {
            search_term: search_term || null,
            page_num: pageParam,
            merchant_uuids: merchantIds,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/merchants/all/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["TRANSACTION_MERCHANTS"] : []),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetAllTransactionMerchantsQuery,
  useLazyGetAllTransactionMerchantsQuery,
  useGetInfiniteAllTransactionMerchantsInfiniteQuery,
  useAssignExistingMerchantMutation,
  useCreateMerchantMutation,
  useDeleteMerchantFromTxnMutation,
  useGetMerchantByIdQuery,
  useLazyGetMerchantByIdQuery,
} = merchantsApi;
